import { Location } from '@angular/common';
import {
  type AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  type OnDestroy,
  type OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  Subject,
  Subscription,
  debounceTime,
  interval,
  take,
  timer,
} from 'rxjs';
import {
  generateIdFromOrgId,
  sortCaseInsensitive,
} from '../../../server/services/utils.service';
import {
  incrementalTaskWeight,
  placeMidWeight,
  taskTitleMaxLength,
} from '../../../server/types/task-types';
import type { DeepReadonly } from '../../../server/util-type';
import { AuthUserService } from '../auth-user.service';
import { DragService, type TaskDragData } from '../drag.service';
import { SettingsService } from '../settings.service';
import { TagService } from '../tag.service';
import {
  type ListFilter,
  type OrgTaskWithUpdates,
  type TaskItem,
  TaskService,
  sortTaskItemByParentWeight,
} from '../task.service';
import { UtilsService } from '../utils.service';

@Component({
  selector: 'app-task',
  templateUrl: './task.component.html',
  styleUrls: ['./task.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class TaskComponent implements OnInit, AfterViewInit, OnDestroy {
  private authUserService = inject(AuthUserService);
  taskService = inject(TaskService);
  private dragService = inject(DragService);
  private settingsService = inject(SettingsService);
  private utilsService = inject(UtilsService);
  private tagService = inject(TagService);
  private location = inject(Location);

  authUserId: string;
  task: DeepReadonly<OrgTaskWithUpdates>;
  error = false;
  @Input() listId: string;
  @Input() listFilter?: DeepReadonly<ListFilter>;
  @Input() orgId: string;
  @Input() spaceId: string;
  @Input() taskId: string;
  @Input() contextDepth: number;
  @Input() expandToContextDepth?: number = 0;
  @Input() inTaskDetail?: boolean = false;
  @Input() siblings: TaskItem[];
  @Input() parentTagIds?: readonly string[];
  @Input() preventDragAndEdit?: boolean;

  @Input() set moveCursorToDeepestChild(
    input: { atStart: boolean } | undefined
  ) {
    if (!input) return;
    const { atStart } = input;
    if (this.showChildren && this.children.length) {
      this.moveCursorToDeepestChildTaskId = undefined;
      // We setTimeout here to force an update
      setTimeout(() => {
        this.moveCursorToDeepestChildAtStart = input.atStart;
        this.moveCursorToDeepestChildTaskId =
          this.children[this.children.length - 1]?.taskId;
      }, 10);
    } else {
      this.moveCursorToThisTask({ atStart });
    }
  }
  moveCursorToDeepestChildTaskId: string | undefined;
  moveCursorToDeepestChildAtStart = false;
  @Output() moveCursorToPriorEmitter = new EventEmitter<{
    atStart: boolean;
  }>();

  @Output() moveCursorToNextEmitter = new EventEmitter<{ atStart: boolean }>();

  @ViewChild('titleTextArea') titleTextArea: ElementRef<HTMLTextAreaElement>;

  completed = false;

  textareaLineHeight = 24; // #titleTextareaLineHeight
  taskTitleMaxLength = taskTitleMaxLength;

  private windowResize = new Subject<number>();
  private windowResizeDebounce = this.windowResize.pipe(debounceTime(500));
  @HostListener('window:resize', ['$event']) onResize(event) {
    this.windowResize.next(event.target.innerWidth);
  }

  titleControl = new FormControl('', { nonNullable: true });

  parentTasks: DeepReadonly<OrgTaskWithUpdates>[];
  parentHeaderSubscriptions = new Subscription();

  dragPosition?: 'above' | 'below' | 'child' | undefined;
  dragElementVisible = false;

  showChildren = false;
  children: TaskItem[] = [];

  themeClass: 'dark-theme' | 'light-theme';

  orphaned = false;
  highlighted = false;

  inputLaunchSource: 'title' | undefined = undefined;
  storedCursorPosition = -1; // -1 is at the end

  suppressBlurDeleteTime = 0;
  removed = false;

  stage: 'current' | 'backlog' | 'complete';
  stageText = '';
  showStage = false;

  inputType: 'assignedTo' | 'tag' | undefined = undefined;

  showAssignedTo = false;
  assignedToName = '';

  tagsDisplay: { _id: string; title: string }[] = [];

  dueText = '';
  scheduleText = '';

  @ViewChild('taskBox') taskBox: ElementRef<HTMLInputElement>;

  taskDetailPreservePath: string;
  taskMessagesPreservePath: string;

  locationVoidFunction?: VoidFunction;
  subscriptions = new Subscription();

  ngOnInit(): void {
    const userOrgObj = this.authUserService.getAuthUserOrgObj({});
    this.authUserId = userOrgObj.user._id;

    const themeSubscription = this.settingsService.themeObservable.subscribe(
      (theme) => (this.themeClass = theme)
    );
    this.subscriptions.add(themeSubscription);

    const { orgId, spaceId, taskId } = this;
    const task = this.taskService.getTaskLocalSync({ orgId, spaceId, taskId });
    if (!task) {
      this.error = true;
      return;
    }

    this.updateFields({ task, initialLoad: true });

    if (
      this.expandToContextDepth &&
      this.contextDepth < this.expandToContextDepth
    )
      this.showChildren = true;

    // Hide children if completed. Only show children if it is the first in the
    // list that is not complete and it is for a specific user in the listFilter
    const firstIncomplete = this.siblings.findIndex((s) => !s.task.completedAt);
    const thisTask = this.siblings.findIndex((s) => s.taskId === this.taskId);
    if (
      !this.task.completedAt &&
      this.listFilter?.assignedTo &&
      this.listFilter?.assignedTo === null &&
      firstIncomplete === thisTask
    ) {
      this.showChildren = true;
    }
    const sub = this.taskService
      .taskUpdates({ taskId })
      .subscribe((t) => this.updateFields({ task: t }));
    this.subscriptions.add(sub);

    // Children
    this.children = this.taskService
      .getChildren({ orgId, spaceId, parentId: taskId })
      .filter((t) => this.task.trashedAt || !t.trashedAt) // Remove trashed children if the parent is not trashed (such as in the trash list)
      .map((t) => ({ taskId: t._id, task: t }));
    this.sortChildren();
    this.children.forEach((child) =>
      this.updateChildSubscription(child.taskId)
    );

    // Add new children as they come in
    const newChildrenSub = this.taskService
      .childTaskUpdateFeed({ parentId: taskId })
      .subscribe((children) => {
        children.forEach((child) => {
          if (
            child.parentId === this.task._id &&
            !this.children.find((c) => c.taskId === child._id) &&
            (this.task.trashedAt || !child.trashedAt) // Remove trashed children if the parent is not trashed (such as in the trash list)
          ) {
            this.children.push({ taskId: child._id, task: child });
            this.sortChildren();
            this.updateChildSubscription(child._id);
          }
        });
      });
    this.subscriptions.add(newChildrenSub);

    // Remove tasks (used in removing blank tasks)
    const removeSubscription = this.taskService
      .taskRemoves({ orgId: this.orgId, spaceId: this.spaceId })
      .subscribe((r) => {
        const index = this.children.findIndex((t) => t.taskId === r.taskId);
        if (index !== -1) this.children.splice(index, 1);
      });
    this.subscriptions.add(removeSubscription);

    this.scrollTaskSetup();
    this.selectTaskSetup();
    this.startTitleSubscription();

    const sizeSub = this.windowResizeDebounce.subscribe((size) => {
      this.recalculateTitleHeight({ calculateContraction: true });
    });
    this.subscriptions.add(sizeSub);

    // This height adjustment is to handle Safari which doesn't have the
    // 'scrollbar-gutter' css functionality
    const heightTimerSub = timer(100).subscribe({
      next: () => {
        this.recalculateTitleHeight({ calculateContraction: false });
      },
    });
    this.subscriptions.add(heightTimerSub);

    this.locationVoidFunction = this.location.onUrlChange((val) =>
      this.findTaskDetailPath(val)
    );
    this.findTaskDetailPath(this.location.path());
  }

  ngAfterViewInit(): void {
    const { taskId, listId } = this;
    this.taskService
      .taskCursor({ taskId, listId })
      .subscribe(({ start, end }) => {
        // setTimeout to prevent ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
          this.taskBox.nativeElement.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest',
          });
          this.titleTextArea.nativeElement.click();
          this.titleTextArea.nativeElement.focus();
          this.titleTextArea.nativeElement.setSelectionRange(start, end);

          this.taskService.setTaskSelectionStart(this.selectionData());
        }, 10);
      });
  }

  // This is a method for checking whether this child has been removed from the
  // list or if it has been repositioned.
  private updateChildSubscription(childTaskId: string) {
    let sub: Subscription | undefined;
    sub = this.taskService
      .taskUpdates({ taskId: childTaskId })
      .subscribe((updatedChild) => {
        const index = this.children.findIndex(
          (c) => c.task._id === childTaskId
        );
        // Remove if the child has been deleted and the parent is not deleted (such as in the trash list)
        if (
          index !== -1 &&
          (updatedChild.parentId !== this.task._id ||
            (!this.task.trashedAt && updatedChild.trashedAt))
        ) {
          // Remove the child from the list as the parent no longer matches
          this.taskService.removeTaskSelection({
            listId: this.listId,
            taskId: childTaskId,
            parentId: this.taskId,
            orphaned: false,
          });
          this.children.splice(index, 1);
          this.sortChildren();
          // We can remove this subscription since it has been removed
          sub?.unsubscribe();
          sub = undefined;
          return;
        }

        if (index >= 0) {
          this.children[index].task = updatedChild;
          this.sortChildren();
        }
      });
    this.subscriptions.add(sub);
  }

  private sortChildren() {
    this.children.sort(sortTaskItemByParentWeight);
  }

  private recalculateTitleHeight({
    calculateContraction,
  }: {
    calculateContraction: boolean;
  }) {
    let sub: Subscription | undefined = undefined;
    sub = interval(1)
      .pipe(take(1))
      .subscribe(() => {
        const nativeTitle = this.titleTextArea.nativeElement;

        if (calculateContraction) {
          nativeTitle.setAttribute('style', 'height: 0px');
          let scrollHeight = nativeTitle.scrollHeight;
          if (scrollHeight < this.textareaLineHeight)
            scrollHeight = this.textareaLineHeight;
          nativeTitle.setAttribute('style', 'height:' + scrollHeight + 'px');
        } else {
          const { clientHeight, scrollHeight } = nativeTitle;
          if (scrollHeight !== clientHeight) {
            nativeTitle.setAttribute('style', 'height:' + scrollHeight + 'px');
          }
        }

        sub?.unsubscribe();
      });
    this.subscriptions.add(sub);
  }

  private startTitleSubscription() {
    let lastValue = this.titleControl.value;
    const sub = this.titleControl.valueChanges.subscribe((value) => {
      if (value === this.task.title) return;
      this.recalculateTitleHeight({ calculateContraction: true });

      if (value.includes('\n')) {
        // Only allow pasting multiple tasks into an empty task
        if (lastValue === '') {
          return this.handlePasteMultipleTasks(value);
        }

        // This case handles a Chrome bug/oddity in which pressing space on the
        // beginning of the 2nd/3rd/etc line of the textarea actually adds a
        // newline character (!?)
        else if (
          value.split('\n').length === 2 &&
          value.split('\n').join('') === lastValue // only the '\n' is added
        ) {
          const { selectionStart, selectionEnd } =
            this.titleTextArea.nativeElement;
          value = value.split('\n').join(' ');
          setTimeout(() => {
            this.titleControl.setValue(value);
            this.titleTextArea.nativeElement.setSelectionRange(
              selectionStart,
              selectionEnd
            );
          }, 10);
        }

        // Pasting multi lines just removes the new line
        else {
          value = value.replace(' \n', ' ').replace('\n', ' ');
          this.titleControl.setValue(value);
        }
      }

      // Prevent tasks too long
      // In the case that a key press or paste causes length > taskTitleMaxLength
      if (value.length > taskTitleMaxLength) {
        // Handle the simple case (type or paste at end)
        if (value.startsWith(lastValue)) {
          value = value.substring(0, taskTitleMaxLength);
          this.titleControl.setValue(value);
          this.titleTextArea.nativeElement.setSelectionRange(
            taskTitleMaxLength,
            taskTitleMaxLength
          );
        } else {
          // Handle the case that a simple keypress or paste happens in the middle
          let cutOccurred = false;
          let pos = 0;
          while (!cutOccurred && pos < lastValue.length) {
            const lastStart = lastValue.substring(0, pos);
            const lastEnd = lastValue.substring(pos);
            if (value.startsWith(lastStart) && value.endsWith(lastEnd)) {
              const leftCutPosition = taskTitleMaxLength - lastEnd.length;
              const newLeftString = value.substring(0, leftCutPosition);
              value = newLeftString + lastEnd;
              this.titleControl.setValue(value);
              const s = taskTitleMaxLength - lastEnd.length;
              this.titleTextArea.nativeElement.setSelectionRange(s, s);
              cutOccurred = true;
            }
            pos++;
          }

          if (!cutOccurred) {
            // Simply cut off the end
            value = value.substring(0, taskTitleMaxLength);
            this.titleControl.setValue(value);
            this.titleTextArea.nativeElement.setSelectionRange(
              taskTitleMaxLength,
              taskTitleMaxLength
            );
          }
        }
      }

      lastValue = value;

      const { orgId, spaceId, _id: taskId } = this.task;
      this.taskService.updateTitleWait({
        orgId,
        spaceId,
        taskId,
        title: value,
      });
    });
    this.subscriptions.add(sub);
  }

  private handlePasteMultipleTasks(value: string) {
    // If someone pastes texts that contain new lines add each line separately
    const titleArray = value
      .split('\n')
      .map((v) => v.trim())
      .filter((v) => v);
    if (titleArray.length > 1) {
      const thisTaskTitle = titleArray.shift(); // pull off the first task
      this.titleControl.setValue(thisTaskTitle ?? '');

      // Add titles on new lines
      if (titleArray.length) {
        const { orgId, spaceId, listId } = this;

        const createdBy = this.authUserId;
        let tagIds: string[];
        let assignedTo: string | undefined;
        let setCurrentAt: boolean;
        let parentId: string | undefined = undefined;
        let parentWeight: number | undefined;
        let parentWeightInc: number | undefined;

        const nextSibling = this.getNextSibling();

        if (this.contextDepth === 0 && this.listFilter) {
          // Take the settings from the list
          if (this.listFilter.assignedTo !== null)
            assignedTo = this.listFilter.assignedTo;
          tagIds = this.listFilter.tagIds?.map((t) => t) ?? [];
          setCurrentAt = this.listFilter.listStage === 'current';
        } else if (this.task.parentId) {
          // inherit parent settings
          const parentTask = this.taskService.getTaskLocalSync({
            orgId,
            spaceId,
            taskId: this.task.parentId,
          });
          if (!parentTask) {
            throw new Error(
              'Could not paste multiple tasks as parent task not found'
            );
          }
          ({ assignedTo } = parentTask);
          tagIds = parentTask.tagIds.map((t) => t);
          setCurrentAt = typeof parentTask.currentAt === 'number';
          if (typeof this.task.parentWeight !== 'number')
            throw new Error('parentWeight not set');
          if (nextSibling) {
            if (typeof nextSibling.parentWeight !== 'number')
              throw new Error('parentWeight not set');
            parentWeightInc =
              (nextSibling.parentWeight - this.task.parentWeight) /
              (titleArray.length + 1);
          } else {
            parentWeightInc = incrementalTaskWeight;
          }

          ({ parentId, parentWeight } = this.task);
        } else {
          throw new Error(
            'unknown state when pasting multiple text lines into task'
          );
        }

        const currentAt = setCurrentAt ? Date.now() : undefined;

        let weightInc: number;
        if (nextSibling) {
          weightInc =
            (nextSibling.weight - this.task.weight) / (titleArray.length + 1);
        } else {
          weightInc = incrementalTaskWeight;
        }
        let weight = this.task.weight;

        titleArray.forEach((title, index) => {
          title = title.trim();
          if (title.length > taskTitleMaxLength)
            title = title.substring(0, taskTitleMaxLength);

          weight += weightInc;
          if (
            typeof parentWeight === 'number' &&
            typeof parentWeightInc === 'number'
          )
            parentWeight += parentWeightInc;
          const { salt, _id: taskId } = generateIdFromOrgId(orgId);

          // Select the last in the list
          if (index === titleArray.length - 1)
            this.taskService.setTaskCursor({ listId, taskId });
          this.taskService.createTask({
            orgId,
            spaceId,
            taskId,
            salt,
            createdBy,
            title,
            tagIds: tagIds,
            currentAt,
            assignedTo,
            weight,
            parentId,
            parentWeight,
          });
        });
      }
    }
  }

  specialKeyPressIntercept(event: KeyboardEvent): void {
    const { orgId, spaceId, _id: taskId } = this.task;

    const { selectionStart, selectionEnd, textLength } =
      this.titleTextArea.nativeElement;

    // Enter
    if (event.key === 'Enter') {
      event.preventDefault();
      // Toggle completedAt
      if (this.utilsService.ctrlOrCmd(event)) {
        // Choose which task to select next
        const focusTaskId =
          this.getNextSibling()?._id ??
          (this.contextDepth === 0 ? this.getPriorSibling()?._id : undefined); // Go to the prior task but only if the task is in a list where the tasks disappear after being deleted
        if (focusTaskId)
          this.taskService.setTaskCursor({
            taskId: focusTaskId,
            listId: this.listId,
          });

        const completedAt = this.task.completedAt ? null : Date.now();
        this.taskService.updateTask(
          { orgId, spaceId, taskId },
          { completedAt }
        );
        return;
      }

      if (this.titleControl.value === '') {
        // Do nothing as we can't set an existing task to have no title, and can't add a new entry below
      } else {
        // Submit
        this.titleTextArea.nativeElement.blur();
        this.createNewTaskAfterPressingEnter();
        this.saveTitle();
      }
    }
    // Delete key (not backspace)
    else if (event.key === 'Delete') {
      if (this.titleControl.value === '') {
        event.preventDefault();

        // Choose which task to select next
        const focusTaskId =
          this.getNextSibling()?._id ??
          this.getPriorSibling()?._id ??
          this.task.parentId;
        if (focusTaskId)
          this.taskService.setTaskCursor({
            taskId: focusTaskId,
            listId: this.listId,
            start: 0,
            end: 0,
          });

        this.trashTask();
      }
    }
    // Backspace
    else if (event.key === 'Backspace') {
      if (
        this.titleControl.value === '' ||
        (event.shiftKey && this.utilsService.ctrlOrCmd(event))
      ) {
        event.preventDefault();

        // Choose which task to select next
        const focusTaskId =
          this.getPriorSibling()?._id ??
          this.getNextSibling()?._id ??
          this.task.parentId;
        if (focusTaskId)
          this.taskService.setTaskCursor({
            taskId: focusTaskId,
            listId: this.listId,
          });

        this.trashTask();
      }
    }
    // TAB
    else if (event.key === 'Tab') {
      if (this.preventDragAndEdit) return;
      // Preserve cursor position (tab handled by taskService)
      this.taskService.setTaskCursor({
        taskId: this.taskId,
        listId: this.listId,
        start: selectionStart,
        end: selectionEnd,
      });
      this.taskService.handleTabEvent(event);
    }
    // UP/DOWN
    else if (
      event.key === 'ArrowDown' &&
      !event.shiftKey &&
      this.utilsService.ctrlOrCmd(event)
    ) {
      this.taskService.expandSelectedSubtasks('expand');
    } else if (
      event.key === 'ArrowUp' &&
      !event.shiftKey &&
      this.utilsService.ctrlOrCmd(event)
    ) {
      this.taskService.expandSelectedSubtasks('collapse');
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      if (selectionStart === 0) {
        if (event.shiftKey) {
          if (event.key === 'ArrowUp') {
            const priorSibling = this.getPriorSibling();
            if (priorSibling) {
              this.shiftSelectTask({ taskId: priorSibling._id });
              this.titleTextArea.nativeElement.blur();
            }
          }
        } else if (selectionStart === selectionEnd) {
          const atStart = event.key === 'ArrowUp';
          this.moveCursorToPriorEmitter.next({ atStart }); // find the deepest visible in previous task
        }
      }
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      if (selectionEnd === textLength) {
        if (event.shiftKey) {
          if (event.key === 'ArrowDown') {
            const nextSibling = this.getNextSibling();
            if (nextSibling) {
              this.shiftSelectTask({ taskId: nextSibling._id });
              this.titleTextArea.nativeElement.blur();
            }
          }
        } else if (selectionStart === selectionEnd) {
          const atStart = event.key === 'ArrowRight';

          // Send to child if open
          if (this.children.length && this.showChildren) {
            this.taskService.setTaskCursor({
              taskId: this.children[0].taskId,
              listId: this.listId,
              start: atStart ? 0 : undefined,
            });
            return;
          }

          this.moveCursorToNextEmitter.next({ atStart });
        }
      }
    }
    // @
    else if (event.key === '@') {
      event.preventDefault();
      this.suppressBlurDelete(); // This is used to prevent a blur event on the title
      this.openInputFromTitle('assignedTo');
    }
    // #
    else if (event.key === '#') {
      event.preventDefault();
      this.suppressBlurDelete(); // This is used to prevent a blur event on the title
      this.openInputFromTitle('tag');
    }
  }

  suppressBlurDelete() {
    this.suppressBlurDeleteTime = Date.now();
  }

  trashTask() {
    const { orgId, spaceId, taskId } = this;
    this.taskService.updateTask(
      { orgId, spaceId, taskId },
      { trashedAt: Date.now() }
    );
  }

  removeFromTrash() {
    if (this.task && this.task.trashedAt)
      this.taskService.updateTask(
        { orgId: this.orgId, spaceId: this.spaceId, taskId: this.taskId },
        { trashedAt: null }
      );
  }

  moveCursorToPriorChildOrHere(atStart: boolean, priorChild?: TaskItem) {
    if (priorChild) {
      this.moveCursorToDeepestChildTaskId = undefined;
      // We setTimeout here to force an update
      setTimeout(() => {
        this.moveCursorToDeepestChildAtStart = atStart;
        this.moveCursorToDeepestChildTaskId = priorChild.taskId;
      }, 10);
    } else {
      this.moveCursorToThisTask({ atStart });
    }
  }

  moveCursorToNextOrParentNext(atStart: boolean, nextChild?: TaskItem) {
    if (nextChild) {
      const taskId = nextChild.taskId;
      const { listId } = this;
      this.taskService.setTaskCursor({ taskId, listId });
    } else {
      // Send focus to parent
      this.moveCursorToNextEmitter.next({ atStart });
    }
  }

  moveCursorToThisTask({ atStart }: { atStart: boolean }) {
    const { taskId, listId } = this;
    this.taskService.setTaskCursor({
      taskId,
      listId,
      start: atStart ? 0 : undefined,
    });
  }

  createNewTaskAfterPressingEnter() {
    const { orgId, spaceId, authUserId: createdBy } = this;

    const { _id: newTaskId, salt } = generateIdFromOrgId(orgId);
    // Focus on task when it is created
    this.taskService.setTaskCursor({ taskId: newTaskId, listId: this.listId });

    const makeTaskAbove =
      this.titleControl.value.length !== 0 &&
      this.titleTextArea.nativeElement.selectionStart === 0;

    if (this.showChildren && this.children.length && !makeTaskAbove) {
      // Create child in this task's child list
      const firstChildTask: TaskItem | undefined = this.children[0];
      const parentWeight = placeMidWeight(
        undefined,
        firstChildTask.task.parentWeight
      );

      const task = this.taskService.createTask({
        taskId: newTaskId,
        salt,
        orgId,
        spaceId,
        createdBy,
        currentAt:
          typeof this.task.currentAt === 'number' ? Date.now() : undefined,
        assignedTo: this.task.assignedTo,
        tagIds: this.task.tagIds.map((t) => t),
        parentId: this.taskId,
        parentWeight,
        weight: parentWeight, // Set because we need need to have a value for weight
        autoDeletable: true,
      });
      return;
    } else if (this.contextDepth === 0 && this.listFilter) {
      // Make at top level
      let weight: number;
      if (makeTaskAbove) {
        const priorSibling = this.getPriorSibling();

        weight = placeMidWeight(priorSibling?.weight, this.task.weight);
      } else {
        const nextSibling = this.getNextSibling();

        weight = placeMidWeight(this.task.weight, nextSibling?.weight);
      }

      const task = this.taskService.createTask({
        taskId: newTaskId,
        salt,
        orgId,
        spaceId,
        createdBy,
        currentAt:
          this.listFilter.listStage === 'current' ? Date.now() : undefined,
        assignedTo:
          this.listFilter.assignedTo === null
            ? undefined
            : this.listFilter.assignedTo,
        tagIds: this.listFilter.tagIds?.map((t) => t) ?? [],
        weight,
        autoDeletable: true,
      });
      return;
    } else if (this.task.parentId) {
      // Create a child at the same level. This includes child tasks in taskDetail view
      let parentWeight: number; // This is actually the parent weight
      if (makeTaskAbove) {
        const priorSibling = this.getPriorSibling();

        parentWeight = placeMidWeight(
          priorSibling?.parentWeight,
          this.task.parentWeight
        );
      } else {
        const subsequentTask = this.getNextSibling();

        parentWeight = placeMidWeight(
          this.task.parentWeight,
          subsequentTask?.parentWeight
        );
      }

      const parentTask = this.taskService.getTaskLocalSync({
        orgId,
        spaceId,
        taskId: this.task.parentId,
      });

      if (!parentTask) {
        throw new Error('Could not create task as parent task not found');
      }

      const task = this.taskService.createTask({
        taskId: newTaskId,
        salt,
        orgId,
        spaceId,
        createdBy,
        currentAt:
          typeof parentTask.currentAt === 'number' ? Date.now() : undefined,
        assignedTo: parentTask.assignedTo,
        tagIds: parentTask.tagIds.map((t) => t),
        parentId: this.task.parentId,
        parentWeight,
        weight: parentWeight, // Set because we need need to have a value for weight
        autoDeletable: true,
      });

      return;
    } else if (this.contextDepth === 0) {
      throw new Error(
        'contextDepth === 0 and neither task.parentId or listFilter not set'
      );
    } else {
      throw new Error('unknown state in createNewTaskAfterPressingEnter');
    }
  }

  createChildTask() {
    const {
      orgId,
      spaceId,
      listId,
      authUserId: createdBy,
      taskId: parentId,
    } = this;
    const { _id: newTaskId, salt } = generateIdFromOrgId(orgId);
    this.taskService.setTaskCursor({ taskId: newTaskId, listId });

    const children = this.taskService.getChildren({ orgId, spaceId, parentId });
    const weightArr = children.map(({ weight: w }) => w);
    const maxChildWeight = weightArr.length
      ? Math.max(...weightArr)
      : undefined;
    const weight = placeMidWeight(maxChildWeight, undefined);

    const parentWeightArr = children
      .map(({ parentWeight: w }) => w)
      .filter((w) => typeof w === 'number');
    const maxChildParentWeight = weightArr.length
      ? Math.max(...parentWeightArr)
      : undefined;
    const parentWeight = placeMidWeight(maxChildParentWeight, undefined);

    const task = this.taskService.createTask({
      taskId: newTaskId,
      salt,
      orgId,
      spaceId,
      parentId: this.taskId,
      createdBy,
      currentAt: this.task.currentAt ? Date.now() : undefined,
      assignedTo: this.task.assignedTo,
      tagIds: this.task.tagIds.map((t) => t),
      weight,
      parentWeight,
      autoDeletable: true,
    });

    this.taskService.expandTask({ listId, taskId: parentId });
  }

  saveTitle() {
    const title = this.titleControl.value;
    const { orgId, spaceId, _id: taskId } = this.task;

    if (this.removed) return;

    this.taskService.updateTask({ orgId, spaceId, taskId }, { title });
  }

  private updateFields({
    task,
    initialLoad = false,
  }: {
    task: DeepReadonly<OrgTaskWithUpdates>;
    initialLoad?: boolean;
  }) {
    const existingTitle = this.titleControl.value;
    this.task = task;

    const parentTask = task.parentId
      ? this.taskService.getTaskLocalSync({
          orgId: task.orgId,
          spaceId: task.spaceId,
          taskId: task.parentId,
        })
      : undefined;

    // Check first if the title has been updated
    if (existingTitle !== task.title) {
      this.titleControl.setValue(task.title);
      this.recalculateTitleHeight({ calculateContraction: !initialLoad });
    }

    this.completed = !!task.completedAt;

    this.orphaned = this.contextDepth === 0 && !!task.parentId;

    this.stage = task.completedAt
      ? 'complete'
      : task.currentAt
      ? 'current'
      : 'backlog';
    const parentStage = !parentTask
      ? undefined
      : parentTask.completedAt
      ? 'complete'
      : parentTask.currentAt
      ? 'current'
      : 'backlog';

    if (this.contextDepth === 0 || task.completedAt) {
      this.showStage = false;
    } else if (
      this.stage !== 'complete' &&
      parentStage &&
      parentStage !== this.stage
    ) {
      this.showStage = true;
    } else {
      this.showStage = false;
    }

    this.stageText =
      this.stage === 'current'
        ? 'Current'
        : this.stage === 'backlog'
        ? 'Backlog'
        : '';

    // Show user
    if (task.assignedTo) {
      this.assignedToName =
        this.authUserService.getUser({
          orgId: this.orgId,
          userId: task.assignedTo,
        })?.name ?? 'Not found';
    } else {
      this.assignedToName = '';
    }
    if (this.contextDepth === 0) {
      // Only show at the top level if the list doesn't specify the assignedTo
      if (this.listFilter?.assignedTo) {
        // this includes this.listFilter?.assignedTo=null
        this.showAssignedTo = false;
      } else {
        this.showAssignedTo = !!this.assignedToName;
      }
    } else if (parentTask) {
      // Only show as a child element assignedTo if different to parent
      this.showAssignedTo = parentTask.assignedTo !== task.assignedTo;
    } else {
      this.showAssignedTo = false;
    }

    // Get tags
    let tagIdsToDisplay: string[];
    if (this.contextDepth === 0) {
      tagIdsToDisplay = this.task.tagIds.filter(
        (t) => !this.listFilter?.tagIds || !this.listFilter?.tagIds.includes(t)
      );
    } else {
      tagIdsToDisplay = this.task.tagIds.filter(
        (t) => !parentTask || !parentTask.tagIds?.includes(t)
      );
    }
    // Remove removed tags
    this.tagsDisplay = this.tagsDisplay.filter((t) =>
      tagIdsToDisplay.includes(t._id)
    );
    // Add new tags
    const { orgId, spaceId } = this;
    tagIdsToDisplay.forEach((tagId) => {
      let sub: Subscription | undefined = undefined;
      sub = this.tagService.getTag({ orgId, spaceId, tagId }).subscribe({
        next: (tag) => {
          const existing = this.tagsDisplay.find((t) => t._id === tag._id);
          if (existing) {
            existing.title = tag.name;
          } else {
            this.tagsDisplay.push({ _id: tag._id, title: tag.name });
          }
          this.tagsDisplay.sort((a, b) =>
            sortCaseInsensitive(a.title, b.title)
          );
          sub?.unsubscribe();
        },
        error: () => {
          sub?.unsubscribe();
        },
      });
      this.subscriptions.add(sub);
    });

    // Format dueAt
    if (task.dueAt) {
      this.dueText = this.utilsService.shortDateTimeFormat({
        time: task.dueAt,
        veryShort: false,
      });
    } else {
      this.dueText = '';
    }

    // Format schedule
    if (task.schedule) {
      this.scheduleText = this.utilsService.shortDateTimeRangeFormat({
        ...task.schedule,
        veryShort: false,
      });
    } else {
      this.scheduleText = '';
    }

    this.getParents();
  }

  getParents() {
    this.parentHeaderSubscriptions.unsubscribe();

    this.parentHeaderSubscriptions = new Subscription();
    this.subscriptions.add(this.parentHeaderSubscriptions);

    const { orgId, spaceId, taskId } = this;
    this.parentTasks = this.taskService.getParents({ orgId, spaceId, taskId });

    // If there is any change in the parents, just get them all again
    this.parentTasks.map((p) => {
      const sub = this.taskService
        .taskUpdates({ taskId: p._id })
        .subscribe(() => this.getParents());

      this.subscriptions.add(sub);
      this.parentHeaderSubscriptions.add(sub);
    });
  }

  private getPriorSibling(): DeepReadonly<OrgTaskWithUpdates> | undefined {
    const index = this.siblings.findIndex((t) => t.task._id === this.taskId);
    return this.siblings[index - 1]?.task;
  }

  private getNextSibling(): DeepReadonly<OrgTaskWithUpdates> | undefined {
    const index = this.siblings.findIndex((t) => t.task._id === this.taskId);
    return this.siblings[index + 1]?.task;
  }

  clickCheck() {
    if (this.completed !== !!this.task.completedAt) {
      if (this.preventDragAndEdit) {
        setTimeout(() => {
          this.completed = !!this.task.completedAt;
        }, 1);
        return;
      }

      // Update task
      const completedAt = this.completed ? Date.now() : null;
      const { orgId, spaceId, _id: taskId } = this.task;
      this.taskService.updateTask({ orgId, spaceId, taskId }, { completedAt });
    }
  }

  onTitleTextareaFocus(event: FocusEvent): void {
    this.titleTextArea.nativeElement.spellcheck = true;
  }

  onTitleTextareaBlur(event: FocusEvent): void {
    setTimeout(() => {
      if (Date.now() - this.suppressBlurDeleteTime < 500) return;

      if (!this.titleControl.value) {
        const { orgId, spaceId, taskId } = this;
        const removed = this.taskService.removeIfBlankAndNotSaved({
          orgId,
          spaceId,
          taskId,
        });
        this.removed = removed;
        if (removed) return;
      }
      this.saveTitle();
    }, 200);

    // Lower down to prevent errors affecting code above
    this.titleTextArea.nativeElement.spellcheck = false;
  }

  // #RequiredForIOS see https://stackoverflow.com/questions/6600950/native-html5-drag-and-drop-in-mobile-safari-ipad-ipod-iphone

  dragStart(event: DragEvent) {
    event.dataTransfer?.setData('text/plain', 'Data stored in DragService'); // see #RequiredForIOS
    event.stopImmediatePropagation();
    const { orgId, spaceId, taskId, listId, contextDepth } = this;
    this.dragService.setData({
      type: 'task',
      orgId,
      spaceId,
      taskId,
      listId,
      contextDepth,
    });
  }

  dragOverTopEdge(event: DragEvent) {
    event.preventDefault(); // see #RequiredForIOS
    event.stopImmediatePropagation();
    const taskId = this.dragEventAllowed(event);
    if (!taskId) return;

    let newDragPosition: 'above' | 'below' | 'child' | undefined;

    if (taskId === this.getPriorSibling()?._id) newDragPosition = undefined;
    else newDragPosition = 'above';

    if (this.dragPosition !== newDragPosition)
      this.dragPosition = newDragPosition;
  }

  dragOver(event: DragEvent) {
    event.preventDefault(); // see #RequiredForIOS
    event.stopImmediatePropagation();
    const taskId = this.dragEventAllowed(event);
    if (!taskId) return;

    this.setDragPosition({ taskId, event });
  }

  private dragEventAllowed(event: DragEvent) {
    const dragData = this.dragService.getData();
    if (!dragData || dragData.type !== 'task') return;
    event.preventDefault(); // Necessary to allow drop to go ahead

    if (!this.dragAllowed(dragData)) return;

    return dragData.taskId;
  }

  private dragAllowed(dragData: TaskDragData): boolean {
    // Prevent drags between orgs and spaces
    if (this.orgId !== dragData.orgId || this.spaceId !== dragData.spaceId)
      return false;

    // Prevent problematic drags to create self-referential loops
    const parentIds = this.parentTasks.map((p) => p._id);
    if (parentIds.includes(dragData.taskId)) {
      this.dragPosition = undefined;
      return false;
    }

    // Prevent drags on self
    if (dragData.taskId === this.taskId) {
      this.dragPosition = undefined;
      return false;
    }

    // No drag on trash etc
    if (this.preventDragAndEdit) return false;

    return true;
  }

  private setDragPosition({
    taskId,
    event,
  }: {
    taskId: string;
    event: DragEvent;
  }) {
    const { top, bottom } = this.taskBox.nativeElement.getBoundingClientRect();
    const height = bottom - top;
    const dragY = event.pageY - top;

    let newDragPosition: 'above' | 'below' | 'child' | undefined;

    if (dragY < height / 2) {
      if (taskId === this.getPriorSibling()?._id) newDragPosition = undefined;
      else newDragPosition = 'above';
    } else {
      if (this.showChildren && this.children.length) {
        if (taskId === this.children[0]?.taskId) newDragPosition = undefined;
        else newDragPosition = 'child';
      } else {
        if (taskId === this.getNextSibling()?._id) newDragPosition = undefined;
        else newDragPosition = 'below';
      }
    }

    if (this.dragPosition !== newDragPosition)
      this.dragPosition = newDragPosition;
  }

  dragLeave(event: DragEvent) {
    event.stopImmediatePropagation();
    this.dragPosition = undefined;
  }

  dragDrop(event: DragEvent) {
    event.preventDefault(); // see #RequiredForIOS
    const dragData = this.dragService.getData();
    if (!dragData || dragData.type !== 'task') return;
    this.drop(dragData);
  }

  drop(dragData: TaskDragData) {
    const dragPosition = this.dragPosition;
    if (!dragPosition) return;
    this.dragPosition = undefined;

    const { orgId, spaceId, taskId } = dragData;
    if (this.spaceId !== spaceId)
      throw new Error('dragging between spaces not implemented');
    const draggedTask = this.taskService.getTaskLocalSync({
      orgId,
      spaceId,
      taskId,
    });
    if (!draggedTask) throw new Error('draggedTask not found');

    const time = Date.now();

    if (dragPosition === 'child') {
      // Insert before the first child
      const child = this.children[0];
      if (!child) return; // We won't send an error state, the last task might have been moved during the drag

      // Ensure that the task inherits the parent data where appropriate
      const assignedTo = this.task.assignedTo;
      const tagIds = this.task.tagIds.map((t) => t);

      // Change current at status based on the parent status
      const parentCurrentAt = this.task.currentAt;
      const currentAt =
        parentCurrentAt && !draggedTask.currentAt
          ? time
          : !parentCurrentAt && draggedTask.currentAt
          ? null
          : undefined;

      if (!child.task.parentWeight)
        throw new Error('parentWeight of child not set');

      const parentWeight = placeMidWeight(undefined, child.task.parentWeight);

      const parentId = this.taskId;
      this.taskService.updateTask(
        { orgId, spaceId, taskId },
        { parentId, parentWeight, assignedTo, currentAt, push: { tagIds } },
        { applyUpdateToChildren: true }
      );
    } else if (dragPosition === 'above' || dragPosition === 'below') {
      if (this.contextDepth === 0) {
        if (!this.listFilter)
          throw new Error('listFilter not set when expected');

        const changeFields = this.taskService.applyListFilter({
          draggedTask,
          listFilter: this.listFilter,
          time,
          draggedTaskFromSameList: dragData.listId === this.listId,
          draggedTaskContextDepth: dragData.contextDepth,
        });
        let weight: number;
        if (dragPosition === 'above') {
          const priorWeight = this.getPriorSibling()?.weight;
          weight = placeMidWeight(priorWeight, this.task.weight);
        } else if (dragPosition === 'below') {
          const subsequentWeight = this.getNextSibling()?.weight;
          weight = placeMidWeight(this.task.weight, subsequentWeight);
        } else {
          throw new Error('unknown dragPosition: ' + dragPosition);
        }

        this.taskService.updateTask(
          { orgId, spaceId, taskId },
          { ...changeFields, weight },
          { applyUpdateToChildren: true }
        );
      } else {
        // Ensure that the task inherits the parent data where appropriate
        const parentTaskId = this.task.parentId;
        if (!parentTaskId) throw new Error('parentTaskId not set');

        const parentTask = this.taskService.getTaskLocalSync({
          orgId: this.orgId,
          spaceId: this.spaceId,
          taskId: parentTaskId,
        });

        if (!parentTask) {
          throw new Error('Could not drop task as parent task not found');
        }

        const assignedTo = parentTask.assignedTo;
        const tagIds = parentTask.tagIds.map((t) => t);

        let parentWeight: number;
        if (dragPosition === 'above') {
          const priorWeight = this.getPriorSibling()?.parentWeight;
          parentWeight = placeMidWeight(priorWeight, this.task.parentWeight);
        } else if (dragPosition === 'below') {
          const subsequentWeight = this.getNextSibling()?.parentWeight;
          parentWeight = placeMidWeight(
            this.task.parentWeight,
            subsequentWeight
          );
        } else {
          throw new Error('unknown dragPosition: ' + dragPosition);
        }

        // Change current at status based on the parent status
        const parentCurrentAt = parentTask.currentAt;
        const currentAt =
          parentCurrentAt && !draggedTask.currentAt
            ? time
            : !parentCurrentAt && draggedTask.currentAt
            ? null
            : undefined;

        const parentId = this.task.parentId;
        this.taskService.updateTask(
          { orgId, spaceId, taskId },
          { parentId, parentWeight, assignedTo, currentAt, push: { tagIds } },
          { applyUpdateToChildren: true }
        );
      }
    }
  }

  dragEnd(event: DragEvent) {
    this.dragService.setData(undefined);
  }

  mouseEnter(event: MouseEvent) {
    // Hack for firefox to make textarea work
    if (navigator.userAgent.toLowerCase().includes('firefox'))
      this.taskBox.nativeElement.draggable = false;
  }

  mouseLeave(event: MouseEvent) {
    // Hack for firefox to make textarea work
    if (navigator.userAgent.toLowerCase().includes('firefox'))
      this.taskBox.nativeElement.draggable = true;
  }

  setCurrent() {
    if (!this.task.currentAt || this.task.completedAt)
      this.taskService.updateTask(
        { orgId: this.orgId, spaceId: this.spaceId, taskId: this.taskId },
        { currentAt: Date.now(), completedAt: null }
      );
  }

  setBacklog() {
    if (this.task.currentAt || this.task.completedAt)
      this.taskService.updateTask(
        { orgId: this.orgId, spaceId: this.spaceId, taskId: this.taskId },
        { currentAt: null, completedAt: null }
      );
  }

  setComplete() {
    if (!this.task.completedAt)
      this.taskService.updateTask(
        { orgId: this.orgId, spaceId: this.spaceId, taskId: this.taskId },
        { completedAt: Date.now() }
      );
  }

  sendTop() {
    if (this.taskId === this.siblings[0]?.task._id) return;
    const firstWeight = this.siblings[0]?.task.weight;
    const weight = placeMidWeight(undefined, firstWeight);
    const { orgId, spaceId, taskId } = this;
    this.taskService.updateTask({ orgId, spaceId, taskId }, { weight });
  }

  selectTaskSetup() {
    const { listId, taskId, orphaned } = this;
    const parentId = this.task.parentId;

    // Get this at startup (because it might be after a tab event)
    this.highlighted = this.taskService.checkIfSelected({
      listId,
      taskId,
      parentId,
      orphaned,
    });

    const selectSub = this.taskService
      .selectObservable({ listId, taskId, parentId, orphaned })
      .subscribe(() => (this.highlighted = true));
    this.subscriptions.add(selectSub);
    const deselectSub = this.taskService
      .deselectObservable({ listId, taskId, parentId, orphaned })
      .subscribe(() => (this.highlighted = false));
    this.subscriptions.add(deselectSub);
    const blurSub = this.taskService
      .blurObservable({ listId, taskId })
      .subscribe(() => this.titleTextArea.nativeElement.blur());
    this.subscriptions.add(blurSub);

    const tabInSub = this.taskService
      .tabInSelectionObservable({ listId, taskId })
      .subscribe(() => {
        const priorSibling = this.getPriorSibling();
        if (!priorSibling) return this.taskService.selectionCannotTabIn();
        else {
          this.taskService.selectionTabIn({ parentId: priorSibling._id }); // No weight puts tabbed tasks at the end of children list in order per parentWeight/weight
          this.taskService.expandTask({ listId, taskId: priorSibling._id });
        }
      });
    this.subscriptions.add(tabInSub);

    const tabOutSub = this.taskService
      .tabOutSelectionObservable({ listId, taskId })
      .subscribe(() => {
        const nextSibling = this.getNextSibling();
        const parentId2 = this.task.parentId;
        if (parentId2) {
          const start = this.task.parentWeight;
          const end = nextSibling?.parentWeight;
          if (!start)
            throw new Error(
              'parentWeight not set but parentId is set: ' + this.task._id
            );
          this.taskService.selectedTabOutToParent({
            parentId: parentId2,
            parentWeight: { start, end },
          });
        } else {
          const start = this.task.weight;
          const end = nextSibling?.weight;
          this.taskService.selectedTabOutNoParent({ weight: { start, end } });
        }
      });
    this.subscriptions.add(tabOutSub);

    const arrowSub = this.taskService
      .arrowObservable({ listId, taskId })
      .subscribe(({ key, shiftKey }) => {
        const selectTaskId =
          key === 'ArrowUp'
            ? this.getPriorSibling()?._id
            : this.getNextSibling()?._id;
        if (selectTaskId) {
          if (shiftKey) {
            this.shiftSelectTask({ taskId: selectTaskId });
            this.titleTextArea.nativeElement.blur();
          } else {
            if (key === 'ArrowUp') {
              this.taskService.setTaskCursor({
                taskId: selectTaskId,
                listId: this.listId,
                start: 0,
                end: 0,
              });
            } else {
              this.taskService.setTaskCursor({
                taskId: selectTaskId,
                listId: this.listId,
              });
            }
          }
        }
      });
    this.subscriptions.add(arrowSub);

    const subListExpandSub = this.taskService
      .subListExpandObservable({ listId, taskId })
      .subscribe(({ action }) => {
        this.showChildren = action === 'expand';

        if (this.showChildren) this.showChildrenFunction();
        else this.hideChildrenFunction();
      });
    this.subscriptions.add(subListExpandSub);

    this.taskService
      .launchDialogObservable({ listId, taskId })
      .subscribe(({ dialog }) => {
        this.openInput(dialog);
      });
  }

  toggleShowChildren() {
    this.showChildren = !this.showChildren;
    if (this.showChildren) this.showChildrenFunction();
    else this.hideChildrenFunction();
  }

  private showChildrenFunction() {
    if (this.children.length === 0) {
      const { orgId, spaceId, authUserId: createdBy } = this;
      const { _id: newTaskId, salt } = generateIdFromOrgId(orgId);

      const parentWeight = placeMidWeight(undefined, undefined);
      const task = this.taskService.createTask({
        taskId: newTaskId,
        salt,
        orgId,
        spaceId,
        createdBy,
        currentAt:
          typeof this.task.currentAt === 'number' ? Date.now() : undefined,
        assignedTo: this.task.assignedTo,
        tagIds: this.task.tagIds.map((t) => t),
        parentId: this.task._id,
        parentWeight,
        weight: parentWeight, // Set because we need need to have a value for weight
      });

      this.taskService.setTaskCursor({
        taskId: task._id,
        listId: this.listId,
      });
      return;
    }
  }

  private hideChildrenFunction() {
    this.deselectAllChildren();
  }

  private scrollTaskSetup() {
    const { taskId } = this;
    const sub = this.taskService
      .getScrollObservable({ taskId })
      .subscribe(({ isTargetTask }) => {
        if (isTargetTask) {
          setTimeout(() => {
            this.titleTextArea.nativeElement.focus();
            const pos = this.task.title.length;
            this.titleTextArea.nativeElement.setSelectionRange(pos, pos);
            this.taskService.setTaskSelectionStart(this.selectionData());
            this.taskService.cancelScrollTaskIntoView();
          }, 10);
        } else {
          this.showChildren = true;
        }
      });

    this.subscriptions.add(sub);
  }

  clickSelectHandler(event: MouseEvent) {
    event.stopImmediatePropagation(); // Prevent propagation through the tree (to avoid selecting parent elements/tasks)

    if (event.shiftKey) {
      const shiftSelectSuccess = this.shiftSelectTask({ taskId: this.taskId });
      if (shiftSelectSuccess) return;
      // else do regular click below
    }

    if (this.utilsService.ctrlOrCmd(event)) {
      this.taskService.controlToggleSelection(this.selectionData());
      event.preventDefault();
      return;
    }

    // Just click which sets selection start
    this.taskService.setTaskSelectionStart(this.selectionData());
  }

  shiftSelectTask({ taskId }: { taskId: string }) {
    const selectionStart = this.taskService.getSelectionStart();
    if (selectionStart) {
      const startIndex = this.siblings.findIndex(
        (s) => s.taskId === selectionStart.taskId
      );
      const selectionStartIsASibling = startIndex >= 0;
      if (selectionStartIsASibling) {
        const endIndex = this.siblings.findIndex((s) => s.taskId === taskId);
        if (endIndex >= 0) {
          this.setSelectionData({ startIndex, endIndex });
          return true;
        }
      }
    }
  }

  setSelectionData({
    startIndex,
    endIndex,
  }: {
    startIndex: number;
    endIndex: number;
  }) {
    const arrayStart = startIndex < endIndex ? startIndex : endIndex;
    const count = Math.abs(startIndex - endIndex) + 1;
    const selections = this.siblings
      .slice(arrayStart, arrayStart + count)
      .map((s) => ({
        orgId: s.task.orgId,
        spaceId: s.task.spaceId,
        listId: this.listId,
        taskId: s.taskId,
        parentId: s.task.parentId,
        orphaned: this.contextDepth === 0 && !!s.task.parentId,
      }));
    const endItem = this.siblings[endIndex];
    const selectionEnd = {
      orgId: endItem.task.orgId,
      spaceId: endItem.task.spaceId,
      listId: this.listId,
      taskId: endItem.taskId,
      parentId: endItem.task.parentId,
      orphaned: this.contextDepth === 0 && !!endItem.task.parentId,
    };
    this.taskService.shiftSetSelections(selections, selectionEnd);
  }

  selectionData() {
    const { orgId, spaceId, taskId, listId } = this;
    const parentId = this.task.parentId;
    const orphaned = this.contextDepth === 0 && !!parentId;

    const data: {
      orgId: string;
      spaceId: string;
      taskId: string;
      listId: string;
      parentId: string | undefined;
      orphaned: boolean;
    } = { orgId, spaceId, taskId, listId, parentId, orphaned };

    return data;
  }

  openInput(inputType: 'assignedTo' | 'tag') {
    if (this.preventDragAndEdit) return;
    this.inputLaunchSource = undefined;
    this.inputType = inputType; // Launches input
  }

  openInputFromTitle(inputType: 'assignedTo' | 'tag') {
    if (this.preventDragAndEdit) return;
    this.inputLaunchSource = 'title';
    this.storedCursorPosition = this.titleTextArea.nativeElement.selectionEnd;
    this.inputType = inputType; // Launches input
  }

  returnToCursor(position: 'end' | undefined) {
    this.inputType = undefined;
    setTimeout(() => {
      this.titleTextArea.nativeElement.focus();
      const pos = position === 'end' ? 10000 : this.storedCursorPosition;
      this.titleTextArea.nativeElement.setSelectionRange(pos, pos);
    }, 50);
  }

  typeCharacter(character: string) {
    this.inputType = undefined;

    const current = this.titleControl.value;
    let insertAtPos = this.storedCursorPosition;
    if (insertAtPos === -1) insertAtPos = current.length;

    const newValue = [
      current.slice(0, insertAtPos),
      character,
      current.slice(insertAtPos),
    ].join('');
    this.titleControl.setValue(newValue);
    const i = insertAtPos + 1;
    this.titleTextArea.nativeElement.focus();
    this.titleTextArea.nativeElement.setSelectionRange(i, i);
  }

  closeTaskInput() {
    this.inputType = undefined;
  }

  deselectAllChildren() {
    this.children.forEach((child) =>
      this.taskService.removeTaskSelection({
        listId: this.listId,
        taskId: this.taskId,
        parentId: this.taskId,
        orphaned: false,
      })
    );
  }

  findTaskDetailPath(value: string) {
    const path = value.split('?')[0];
    // Remove the task path
    const pathWithoutTaskId = path.split('/task/')[0];
    this.taskDetailPreservePath = pathWithoutTaskId + '/task/' + this.taskId;
    this.taskMessagesPreservePath = this.taskDetailPreservePath + '/thread';
  }

  ngOnDestroy(): void {
    this.saveTitle();

    if (this.locationVoidFunction) this.locationVoidFunction();
    this.subscriptions.unsubscribe();

    this.deselectAllChildren();
  }
}
